/**
 * From a conversation failure to a sentence somebody can act on (`KLQ-20260820-001` phase 1).
 *
 * Same rule as every other `problems.ts` here: branch on the problem's `code`, never on the
 * status (`backend/src/kalqa/core/errors.py`, first paragraph).
 *
 * The map itself is `features/drawings`'s — this screen family has one error vocabulary and a
 * second copy of it would be a second place for `not_found` to stop meaning *"gone, and we do
 * not say whether you were allowed to see it"*. Two codes matter more here than there:
 *
 * - **`cursor_invalid`.** The feed pages by an opaque, signed cursor, and a tampered or stale
 *   one is refused rather than reinterpreted. The right recovery is to start the list again,
 *   so the copy says that instead of "something went wrong".
 * - **`not_found` on a deep link.** A comment on a drawing outside the caller's zones answers
 *   `404` byte-identically to one that never existed. The copy must not distinguish them —
 *   the server spent effort making them identical and a client that guesses undoes it.
 */

import { ApiError } from '@/api/client';
import { apiErrorKey } from '@/features/drawings';
import type { MessageKeys } from '@/lib/i18n';

type DrawingsKey = MessageKeys['drawings'];

/**
 * The message key for a feed or thread failure.
 *
 * Delegates to the screen family's map for everything it does not own, so a new error code
 * added there is answered here too rather than falling to "unexpected".
 */
export function feedErrorKey(error: unknown): DrawingsKey {
  if (error instanceof ApiError && error.code === 'cursor_invalid') return 'feed.error.cursor';
  return apiErrorKey(error);
}
